import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../../components/SEOHead';

declare global {
  interface Window {
    Razorpay: any;
  }
}

// Helper function to get Facebook cookies
function getFacebookCookies() {
  const cookies = document.cookie.split(';');
  let fbc = '';
  let fbp = '';
  
  cookies.forEach(cookie => {
    const [name, value] = cookie.trim().split('=');
    if (name === '_fbc') fbc = value;
    if (name === '_fbp') fbp = value;
  });
  
  return { fbc, fbp };
}

// Helper function to send Meta CAPI event
async function sendMetaEvent(eventName: string, userData: any, customData: any = {}) {
  try {
    const { fbc, fbp } = getFacebookCookies();
    
    const eventPayload = {
      eventName,
      eventData: {
        sourceUrl: window.location.href,
        fbc,
        fbp,
        customData,
      },
      userData,
    };

    const response = await fetch(
      `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/meta-capi-track`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventPayload),
      }
    );

    const result = await response.json();
    console.log('Meta CAPI Event Sent:', eventName, result);
  } catch (error) {
    console.error('Meta CAPI Error:', error);
  }
}

// Helper function to save checkout details to database
async function saveCheckoutDetails(
  formData: { name: string; email: string; phone: string },
  paymentStatus: string = 'initiated',
  orderId?: string,
  paymentId?: string,
  amount: number = 99
) {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/save-checkout-details`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          paymentStatus,
          orderId,
          paymentId,
          amount,
        }),
      }
    );

    const result = await response.json();
    console.log('Checkout details saved:', result);
    return result;
  } catch (error) {
    console.error('Error saving checkout details:', error);
  }
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "CheckoutPage",
    "name": "Checkout - AI Tools Workshop",
    "url": `${siteUrl}/checkout`,
    "description": "Complete your enrollment for the AI Tools Workshop. Pay ₹99 and get started with mastering AI tools.",
    "isPartOf": {
      "@type": "WebSite",
      "name": "AI Mastery",
      "url": siteUrl
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Enhanced Razorpay loading with retry logic
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 3;

    const loadRazorpay = () => {
      if (window.Razorpay) {
        setRazorpayLoaded(true);
        setLoadError(false);
        return;
      }

      const existingScript = document.querySelector('script[src*="razorpay"]');
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      
      script.onload = () => {
        console.log('Razorpay loaded successfully');
        setRazorpayLoaded(true);
        setLoadError(false);
      };
      
      script.onerror = () => {
        console.error('Failed to load Razorpay, attempt:', retryCount + 1);
        
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(() => {
            loadRazorpay();
          }, 1000 * retryCount);
        } else {
          setLoadError(true);
          console.error('Failed to load Razorpay after', maxRetries, 'attempts');
        }
      };
      
      document.head.appendChild(script);
    };

    loadRazorpay();

    return () => {
      const script = document.querySelector('script[src*="razorpay"]');
      if (script) {
        script.remove();
      }
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const bottomStrip = document.querySelector('.fixed.bottom-0.left-0.right-0.z-50') as HTMLElement;
    if (bottomStrip) {
      bottomStrip.style.display = 'none';
    }
    
    return () => {
      if (bottomStrip) {
        bottomStrip.style.display = 'block';
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isProcessing) return;

    if (!razorpayLoaded || !window.Razorpay) {
      if (loadError) {
        alert('Unable to load payment system. Please check your internet connection and refresh the page.');
      } else {
        alert('Payment system is still loading. Please wait a moment and try again.');
      }
      return;
    }
    
    setIsProcessing(true);

    try {
      const totalAmount = 99;
      const amountInPaise = totalAmount * 100;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const orderResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-razorpay-order`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount: amountInPaise,
            currency: 'INR',
            receipt: `receipt_${Date.now()}`,
            notes: {
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
            },
          }),
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (!orderResponse.ok) {
        throw new Error('Failed to create order');
      }

      const data = await orderResponse.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create order');
      }

      Promise.all([
        sendMetaEvent('InitiateCheckout', {
          email: formData.email,
          phone: formData.phone,
          firstName: formData.name.split(' ')[0],
          lastName: formData.name.split(' ').slice(1).join(' '),
        }, {
          content_name: 'AI Tools Workshop',
          content_category: 'Education',
          value: totalAmount,
          currency: 'INR',
        }),
        saveCheckoutDetails(formData, 'initiated', undefined, undefined, totalAmount)
      ]).catch(console.error);

      if (!window.Razorpay) {
        throw new Error('Payment system became unavailable. Please refresh and try again.');
      }

      const options = {
        key: data.key_id,
        amount: data.order.amount,
        currency: data.order.currency,
        name: 'AI Mastery',
        description: 'AI Tools Workshop',
        order_id: data.order.id,
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone,
        },
        theme: {
          color: '#0d9488',
        },
        handler: async function (response: any) {
          Promise.all([
            saveCheckoutDetails(
              formData,
              'completed',
              data.order.id,
              response.razorpay_payment_id,
              totalAmount
            ),
            sendMetaEvent('Purchase', {
              email: formData.email,
              phone: formData.phone,
              firstName: formData.name.split(' ')[0],
              lastName: formData.name.split(' ').slice(1).join(' '),
            }, {
              content_name: 'AI Tools Workshop',
              content_category: 'Education',
              value: totalAmount,
              currency: 'INR',
              transaction_id: response.razorpay_payment_id,
            }),
            fetch(
              `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-welcome-email`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  name: formData.name,
                  email: formData.email,
                }),
              }
            )
          ]).catch(console.error);

          console.log('Payment Response:', response);
          navigate('/thank-you');
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      
      razorpay.on('payment.failed', function (response: any) {
        alert(`Payment Failed: ${response.error.description}`);
        console.error('Payment Error:', response.error);
        setIsProcessing(false);
      });

      razorpay.open();

    } catch (error: any) {
      console.error('Error:', error);
      
      if (error.name === 'AbortError') {
        alert('Request timed out. Please check your internet connection and try again.');
      } else {
        alert('Something went wrong. Please try again.');
      }
      
      setIsProcessing(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Checkout - AI Tools Workshop | AI Mastery"
        description="Complete your enrollment for the AI Tools Workshop. Pay ₹99 and get started with mastering AI tools."
        keywords="AI workshop checkout, enroll AI course, AI tools payment"
        canonical="/checkout"
        schema={schema}
      />

      <div className="min-h-screen bg-white">
        {/* Sticky Header */}
        <div className="sticky top-0 z-50 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 shadow-lg">
          <div className="px-3 sm:px-4 py-3 sm:py-4">
            <div className="max-w-7xl mx-auto text-center">
              <a 
                href="/" 
                className="text-gray-900 hover:text-white transition-colors cursor-pointer font-bold text-xl sm:text-2xl md:text-3xl uppercase inline-block"
                aria-label="Go to AI Mastery homepage"
              >
                AI Mastery
              </a>
              <div className="text-xs sm:text-sm font-semibold text-gray-900 mt-1">
                CONGRATS! YOU ARE JUST ONE STEP AWAY
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8">
          {/* Headline */}
          <div className="text-center mb-4 sm:mb-6">
            <h2 className="text-gray-700 font-medium text-sm sm:text-base mb-2">
              Anyone from any field can get Job.
            </h2>
            
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
              Simply Pay{' '}
              <span className="text-red-600">
                <span className="line-through font-normal text-base sm:text-lg">₹4,999</span> ₹99
              </span>{' '}
              and Get Call From Our Placement Cell for Getting Job
            </h1>
          </div>

          {/* Checkout Form */}
          <div className="max-w-lg mx-auto">
            <div className="rounded-xl sm:rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              {/* Form Header */}
              <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-4 sm:py-5 text-center">
                <div className="text-xs sm:text-sm uppercase tracking-wider">AI Tools Workshop</div>
              </div>

              {/* Form Body */}
              <form onSubmit={handleSubmit} className="p-4 sm:p-6 md:p-8 bg-white space-y-4">
                {/* Full Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                    Full Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 sm:py-3.5 text-sm sm:text-base rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                    Email Address <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="example@example.com"
                    className="w-full px-4 py-3 sm:py-3.5 text-sm sm:text-base rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                    Mobile Number <span className="text-xs font-normal text-gray-600">(WhatsApp)</span>
                    <span className="text-red-600 ml-1">*</span>
                  </label>
                  <div className="flex">
                    <div className="flex items-center justify-center px-3 sm:px-4 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50">
                      <span className="text-sm sm:text-base whitespace-nowrap font-medium">🇮🇳 +91</span>
                    </div>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      maxLength={10}
                      value={formData.phone.replace('+91', '')}
                      onChange={(e) => setFormData({ ...formData, phone: '+91' + e.target.value.replace(/\D/g, '').slice(0, 10) })}
                      placeholder="9876543210"
                      className="flex-1 px-4 py-3 sm:py-3.5 text-sm sm:text-base rounded-r-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                    />
                  </div>
                </div>

                {/* WhatsApp Notice */}
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 bg-emerald-50 px-3 py-2 rounded-lg">
                  <i className="ri-whatsapp-line text-emerald-600 text-lg"></i>
                  <span>You will get updates on your WhatsApp</span>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isProcessing || !razorpayLoaded || loadError}
                  className="w-full py-3.5 sm:py-4 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white text-base sm:text-lg font-bold rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg"
                >
                  {loadError ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-error-warning-line text-xl"></i>
                      <span className="text-sm sm:text-base">Payment Error - Refresh Page</span>
                    </span>
                  ) : !razorpayLoaded ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm sm:text-base">Loading Payment...</span>
                    </span>
                  ) : isProcessing ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm sm:text-base">Processing...</span>
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-lock-line text-lg"></i>
                      Pay ₹99 Now
                    </span>
                  )}
                </button>

                {/* Status Message */}
                {!razorpayLoaded && !loadError && (
                  <p className="text-center text-xs text-gray-500">
                    Setting up secure payment...
                  </p>
                )}

                {loadError && (
                  <p className="text-center text-xs text-red-600">
                    Unable to load payment system. Please refresh.
                  </p>
                )}
              </form>
            </div>
          </div>

          {/* Certificate Notice */}
          <div className="text-center mt-6 sm:mt-8 px-4">
            <div className="inline-flex items-start gap-3 bg-teal-50 px-4 sm:px-6 py-3 sm:py-4 rounded-xl">
            </div>
          </div>

          {/* Trust Badge */}
          <div className="mt-6 sm:mt-8">
            <div className="bg-gray-50 py-4 sm:py-6 rounded-xl">
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
